/**
 * Utility functions for safe API response handling
 * 
 * This module provides helper functions to safely parse API responses
 * and handle cases where the server returns HTML instead of JSON
 * (e.g., error pages, CloudFront errors, etc.)
 */

/**
 * Interface for parsed API response
 */
export interface ParsedResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  statusCode?: number;
}

/**
 * Safely parse a Response object as JSON, handling HTML responses gracefully
 * 
 * @param response - The fetch Response object
 * @param context - Optional context string for better error messages
 * @returns Parsed response with success status and data/error
 * 
 * @example
 * ```typescript
 * const response = await fetch('/api/endpoint');
 * const result = await safeJsonParse(response, 'fetching user data');
 * 
 * if (!result.success) {
 *   console.error('API Error:', result.error);
 *   alert(result.error);
 *   return;
 * }
 * 
 * // Use result.data safely
 * console.log(result.data);
 * ```
 */
export async function safeJsonParse<T = any>(
  response: Response,
  context: string = 'API request'
): Promise<ParsedResponse<T>> {
  const statusCode = response.status;
  
  // Check if response is OK (2xx status)
  if (!response.ok) {
    // Check content type before trying to parse
    const contentType = response.headers.get('content-type');
    let errorMessage = `HTTP ${statusCode}: ${response.statusText}`;
    
    if (contentType && contentType.includes('application/json')) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.detail || errorData.message || errorMessage;
        console.error(`Error in ${context}:`, errorData);
        
        return {
          success: false,
          error: errorMessage,
          statusCode,
        };
      } catch (parseError) {
        console.error(`Failed to parse error response as JSON for ${context}:`, parseError);
        return {
          success: false,
          error: `${errorMessage} (Failed to parse error details)`,
          statusCode,
        };
      }
    } else {
      // Response is not JSON (likely HTML error page from CloudFront, ALB, etc.)
      const textResponse = await response.text();
      const preview = textResponse.substring(0, 200);
      console.error(`Non-JSON error response for ${context}:`, preview);
      
      // Provide user-friendly message based on status code
      if (statusCode === 404) {
        errorMessage = 'The requested resource was not found. Please try again or contact support.';
      } else if (statusCode === 500 || statusCode === 502 || statusCode === 503) {
        errorMessage = 'The server encountered an error. Please try again in a few moments.';
      } else if (statusCode === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (statusCode === 403) {
        errorMessage = 'Access denied. You do not have permission for this action.';
      }
      
      return {
        success: false,
        error: errorMessage,
        statusCode,
      };
    }
  }
  
  // Response is OK, try to parse as JSON
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const textResponse = await response.text();
    const preview = textResponse.substring(0, 200);
    console.error(`Expected JSON response but got for ${context}:`, preview);
    
    return {
      success: false,
      error: 'Server returned an unexpected response format. Please try again or contact support.',
      statusCode,
    };
  }
  
  try {
    const data = await response.json();
    return {
      success: true,
      data: data as T,
      statusCode,
    };
  } catch (parseError) {
    console.error(`Failed to parse successful response as JSON for ${context}:`, parseError);
    return {
      success: false,
      error: 'Failed to parse server response. Please try again.',
      statusCode,
    };
  }
}

/**
 * Make a fetch request with automatic JSON parsing and error handling
 * 
 * @param url - The URL to fetch
 * @param options - Fetch options (headers, method, body, etc.)
 * @param context - Optional context string for better error messages
 * @returns Parsed response with success status and data/error
 * 
 * @example
 * ```typescript
 * const result = await safeFetch<UserData>(
 *   '/api/user/profile',
 *   {
 *     method: 'GET',
 *     headers: { 'Authorization': `Bearer ${token}` }
 *   },
 *   'fetching user profile'
 * );
 * 
 * if (!result.success) {
 *   alert(result.error);
 *   return;
 * }
 * 
 * setUserData(result.data);
 * ```
 */
export async function safeFetch<T = any>(
  url: string,
  options?: RequestInit,
  context: string = 'API request'
): Promise<ParsedResponse<T>> {
  try {
    const response = await fetch(url, options);
    return await safeJsonParse<T>(response, context);
  } catch (error) {
    // Network error or other fetch failure
    console.error(`Network error during ${context}:`, error);
    
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'Network error. Please check your connection and try again.';
    
    return {
      success: false,
      error: errorMessage,
    };
  }
}

